<template>
  <div>
    <Dialog :open="openModal" @update:open="onClose">
      <DialogContent class="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Add New Card</DialogTitle>
          <DialogDescription>Merchant Billing Debit Order</DialogDescription>
          <DialogDescription v-if="errorMessage" class="text-red-500">
            {{ errorMessage }}
          </DialogDescription>
        </DialogHeader>
        <SectionDivider />
        <div>
          <LayupSpinner v-if="!checkoutFormLoaded" />
          <div id="payment-form" :class="{ 'h-[550px]': checkoutFormLoaded }"></div>
        </div>
      </DialogContent>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import SectionDivider from '@/components/common/SectionDivider.vue';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { BASE_URL, EMBEDDED_ENTITY_ID } from '@/config';
import { createCard, verifyCard } from '@/services/cards';
import { createException } from '@/utils/monitoring.utils';
import { ref, watch } from 'vue';
import type { ModelCardRequest } from '@layup/api-sdk';

const emit = defineEmits(['disable:modal', 'card:added']);
const props = defineProps<{
  shouldOpenModal: boolean;
}>();

const openModal = ref(false);
const errorMessage = ref('');
const checkoutFormLoaded = ref(false);

watch(
  () => props.shouldOpenModal,
  (newValue) => {
    openModal.value = newValue;
  },
  { immediate: true },
);

const loadCheckoutForm = async () => {
  try {
    const request: ModelCardRequest = {
      defaultCard: true,
      redirectUrl: `${BASE_URL}/merchant`,
    };

    const createdCard = await createCard(request);
    const { checkoutId } = createdCard;

    if ((window as any).Checkout) {
      const checkout = (window as any).Checkout.initiate({
        checkoutId,
        key: EMBEDDED_ENTITY_ID,
        events: {
          onCompleted: () =>
            verifyCard(checkoutId!).then(() => {
              emit('card:added');
              onClose();
            }),
          onCancelled: () => onClose(),
          onExpired: () => onClose(),
        },
        options: {
          theme: {
            brand: {
              primary: '#1295a5',
            },
          },
        },
      });
      checkout.render('#payment-form');
      checkoutFormLoaded.value = true;
    } else {
      createException({ message: 'Checkout SDK not loaded.', checkoutId });
    }
  } catch (error) {
    createException({ message: 'Error loading checkout form.', error });
  }
};

const onClose = () => {
  openModal.value = false;
  emit('disable:modal', false);
};

const init = async () => {
  try {
    await loadCheckoutForm();
  } catch (error: any) {
    console.error('Error loading checkout form:', error);
    errorMessage.value = 'Error loading checkout form. Please try again later.';
    if (error?.response?.data) {
      errorMessage.value = error.response.data;
    }
  }
};
init();
</script>
